/**
 * Status-changed-to-hold dialog: hold-until date plus a searchable reason.
 *
 * Owns form state and stages a `hold` auth-status comment in UserStore on
 * Save. Field layout and the portaled reason select live in
 * {@link StatusChangedToHoldModalBody}; dialog overflow is owned by
 * `statusChangedToHoldModal.scss`.
 *
 * @module statusChangedToHoldModal
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { CptCodesTableRowModel } from "../../../data-model/cptCodesTableModel";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { createDataForCommentsAndPushToLocalStorage } from "../StatusUpdateModal/utils/createDataForCommentsAndPushToLocalStorage";
import "./statusChangedToHoldModal.scss";
import StatusChangedToHoldModalBody from "./statusChangedToHoldModalBody";

/**
 * Props for {@link StatusChangedToHoldModal}.
 */
interface StatusChangedToHoldModalProps {
  /** Dismisses the dialog without persisting the hold date. */
  onClose: () => void;
  /** Called with the chosen hold-until date when Save is clicked. */
  onSave: (selectedDate: Date) => void;
  /** When true, Save shows a saving label and is disabled. */
  loading?: boolean;
  /** CPT row being held; supplies the j-code for the saved comment. */
  rowData?: CptCodesTableRowModel;
}

/**
 * Hold-until dialog shown after an order status changes to Hold.
 *
 * Save stays disabled until a date, a reason, and (for "Other") a
 * free-text reason are all provided. The parent still receives only the
 * date via {@link StatusChangedToHoldModalProps.onSave}; the comment text
 * is "hold until {date}", or "{other reason}, hold until {date}" when
 * Other is selected.
 *
 * @param props - Close handler, save emitter, loading flag, and CPT row
 * @returns Status Changed modal shell with date and hold-reason fields
 */
const StatusChangedToHoldModal = (props: StatusChangedToHoldModalProps) => {
  const { loading = false } = props;
  const { generalOrderDetails } = useDataAccumulatorForSingleOrder();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [date, setDate] = useState<Date | null>(null);
  const [reason, setReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const otherReasonMissing = reason === "Other" && otherReason.trim() === "";

  /**
   * Stages the hold comment (date, reason, j-code) into UserStore, then
   * emits the hold-until date to the parent for the remote update.
   *
   * @returns void
   */
  const handleSave = () => {
    if (!date) return;
    const orderId = generalOrderDetails?.order?.order_id;
    const otherReasonText = otherReason.trim();
    const holdUntilPhrase = `hold until ${moment(date).format("DD MMM YYYY")}`;
    const comment =
      reason === "Other" && otherReasonText
        ? `${otherReasonText}, ${holdUntilPhrase}`
        : holdUntilPhrase;
    if (orderId && user?.id) {
      createDataForCommentsAndPushToLocalStorage(
        orderId,
        comment,
        reason,
        props?.rowData?.jCodes?.data ?? "",
        "hold",
        user?.email ?? "",
      );
    }
    props.onSave(date);
  };

  return (
    <Modal
      dialogId={ModalId.STATUS_CHANGED_TO_HOLD_MODAL}
      title="Status Changed"
      onCancel={props.onClose}
      onClose={props.onClose}
      onSave={handleSave}
      saveButtonText={loading ? "Saving..." : "Save"}
      cancelText="Cancel"
      disableSave={loading || !date || otherReasonMissing || !reason}
      maxHeightPercentage={40}
    >
      <StatusChangedToHoldModalBody
        date={date}
        onDateChange={setDate}
        reason={reason}
        onReasonChange={setReason}
        otherReason={otherReason}
        onOtherReasonChange={setOtherReason}
      />
    </Modal>
  );
};

export default StatusChangedToHoldModal;
